import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Utils & misc
import cn from '../../../utils/cn';
import { parseFontAwesomeClass } from '../../../utils/fontawesome/config';
// eslint-disable-next-line import/no-unresolved
import Loader from '../../../assets/loaders/circle-loader.svg?react';
// Style
import style from './Button.module.css';
// Components
import Picto, { availablePictos } from '../Picto/Picto';

/**
 * Render a button icon from either a FontAwesome class (API-provided) or a Picto key.
 * @param {keyof typeof availablePictos | string} icon
 * @param {string} className
 * @returns {JSX.Element | null}
 */
const renderButtonIcon = (icon, className) => {
  const iconDefinition = parseFontAwesomeClass(icon);
  if (iconDefinition) {
    return <FontAwesomeIcon icon={/** @type {any} */ (iconDefinition)} className={className} />;
  }
  if (icon && Object.prototype.hasOwnProperty.call(availablePictos, icon)) {
    const pictoIcon = /** @type {keyof typeof availablePictos} */ (icon);
    return <Picto icon={pictoIcon} className={className} />;
  }
  return null;
};

/**
 * Button component - A customizable button with various states and styles
 * @param {object} params
 * @param {React.ReactNode} [params.children] - Button content (text or elements)
 * @param {(event: React.MouseEvent<HTMLButtonElement>) => void} [params.onClick] - Click handler function
 * @param {'primary'|'primary100'|'neutral00'|'alert'|'success'|'no-background'|'no-background-black'} [params.color='primary'] - Button color variant
 * @param {'small'|'medium'} [params.size='medium'] - Button size
 * @param {boolean} [params.isSubmit=false] - Whether button should submit form
 * @param {boolean} [params.isLoading=false] - Whether button is in loading state
 * @param {keyof typeof import('../Picto/Picto').availablePictos | string} [params.icon] - Icon name from available pictos or FontAwesome icon class (e.g. "fal fa-check")
 * @param {'left'|'right'} [params.iconPosition='left'] - Icon position relative to text
 * @param {string} [params.className] - Additional CSS classes
 * @param {boolean} [params.disabled=false] - Whether button is disabled
 * @param {string} [params.form] - Form ID to associate with (for submit buttons)
 * @param {string} [params.ariaLabel] - Accessible name when the visible label is insufficient or hidden
 * @returns {JSX.Element} Button component
 */
function Button({
  onClick,
  isSubmit = false,
  isLoading = false,
  icon,
  iconPosition = 'left',
  children,
  className,
  disabled = false,
  color = 'primary',
  size = 'medium',
  form,
  ariaLabel,
}) {
  return (
    <button
      type={isSubmit ? 'submit' : 'button'}
      onClick={onClick}
      disabled={disabled || isLoading}
      form={form}
      aria-label={ariaLabel}
      className={cn([
        style.button,
        style[color],
        style[size],
        style[iconPosition],
        ...(isLoading ? [style['is-loading']] : []),
        ...(!children && icon ? [style['only-icon']] : []),
        ...(className ? [className] : []),
      ])}
    >
      {isLoading ? <Loader className={style.loader} /> : null}
      {!isLoading && icon ? renderButtonIcon(icon, style.icon) : null}
      {children}
    </button>
  );
}

export default Button;
